import React, { useState } from 'react';
import { BellIcon, SearchIcon, MenuIcon, ShieldIcon, Building2Icon, UserIcon, PlusIcon, HeadphonesIcon, CreditCardIcon, XIcon } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
interface TopBarProps {
  userRole: string | null;
  onMenuToggle: () => void;
}
export function TopBar({
  userRole,
  onMenuToggle
}: TopBarProps) {
  const location = useLocation();
  const {
    notifications,
    paymentInfo,
    markNotificationRead
  } = useAppContext();
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const unreadCount = notifications.filter(n => !n.read).length;
  const getPageTitle = (pathname: string) => {
    if (pathname.includes('/super-admin')) return 'Platform Management';
    if (pathname.includes('/admin/users')) return 'User Management';
    if (pathname.includes('/onboarding')) return 'Organization Setup';
    switch (pathname) {
      case '/':
        return 'Dashboard';
      case '/booking':
        return 'Book Travel';
      case '/trips':
        return 'My Trips';
      case '/approvals':
        return 'Approvals';
      case '/budget':
        return 'Budget Management';
      case '/travelers':
        return 'Traveler Tracking';
      case '/reports':
        return 'Reports & Analytics';
      case '/settings':
        return 'Settings';
      case '/operations':
        return 'Operations Queue';
      case '/audit-logs':
        return 'Audit Logs';
      case '/support':
        return 'Support Center';
      case '/events':
        return 'Events & Conferences';
      case '/payments':
        return 'Payments & Billing';
      case '/integrations':
        return 'Integrations';
      case '/refund-policies':
        return 'Refund Policies';
      case '/expenses':
        return 'Expenses';
      case '/notifications':
        return 'Notifications';
      default:
        return 'Dashboard';
    }
  };
  const getRoleBadge = () => {
    switch (userRole) {
      case 'super_admin':
        return <span className="hidden sm:inline-flex items-center rounded-full bg-gold-100 px-2.5 py-0.5 text-xs font-medium text-gold-800" data-id="element-147">
            <ShieldIcon className="mr-1 h-3 w-3" data-id="element-148" /> Super Admin
          </span>;
      case 'tenant_admin':
        return <span className="hidden sm:inline-flex items-center rounded-full bg-navy-100 px-2.5 py-0.5 text-xs font-medium text-navy-800" data-id="element-149">
            <Building2Icon className="mr-1 h-3 w-3" data-id="element-150" /> Admin
          </span>;
      case 'manager':
        return <span className="hidden sm:inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800" data-id="element-151">
            <UserIcon className="mr-1 h-3 w-3" data-id="element-152" /> Manager
          </span>;
      default:
        return null;
    }
  };
  const recentNotifs = notifications.slice(0, 5);
  return <header className="fixed top-0 right-0 z-30 ml-0 lg:ml-64 flex h-16 w-full lg:w-[calc(100%-16rem)] items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6 shadow-sm" data-id="element-153">
      <div className="flex items-center gap-3" data-id="element-154">
        {/* Hamburger - mobile only */}
        <button onClick={onMenuToggle} className="lg:hidden p-1.5 -ml-1 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors" aria-label="Toggle sidebar" data-id="element-155">
          <MenuIcon className="h-6 w-6" data-id="element-156" />
        </button>
        <div className="min-w-0" data-id="element-157">
          <h1 className="text-lg sm:text-xl font-semibold text-navy-900 truncate" data-id="element-158">
            {getPageTitle(location.pathname)}
          </h1>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3" data-id="element-159">
        {/* Search - hidden on mobile */}
        <div className="relative hidden md:block" data-id="element-160">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-161" />
          <input type="text" placeholder="Search..." className="h-9 w-48 lg:w-56 rounded-lg border border-slate-300 bg-slate-50 pl-10 pr-4 text-sm focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600" data-id="element-162" />
        </div>

        {/* Quick Actions - hidden on small mobile */}
        <Link to="/booking" className="hidden sm:flex items-center gap-1.5 rounded-lg bg-navy-900 px-3 py-2 text-xs font-semibold text-white hover:bg-navy-700 transition-colors" data-id="element-163">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-164" />
          <span className="hidden md:inline" data-id="element-165">New Booking</span>
        </Link>

        {/* Credit Balance */}
        {(userRole === 'tenant_admin' || userRole === 'manager') && <Link to="/payments" className="hidden lg:flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-166">
            <CreditCardIcon className="h-3.5 w-3.5 text-gold-600" data-id="element-167" />
            <span className="text-gold-700 font-bold" data-id="element-168">
              ${(paymentInfo.availableCredit / 1000).toFixed(0)}k
            </span>
            <span className="text-slate-400" data-id="element-169">credit</span>
          </Link>}

        {/* Notifications */}
        <div className="relative" data-id="element-170">
          <button onClick={() => setShowNotifDropdown(!showNotifDropdown)} className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" data-id="element-171">
            <BellIcon className="h-5 w-5" data-id="element-172" />
            {unreadCount > 0 && <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white" data-id="element-173">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>}
          </button>

          {showNotifDropdown && <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifDropdown(false)} data-id="element-174" />
              <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm rounded-xl bg-white shadow-xl ring-1 ring-slate-900/5 z-50" data-id="element-175">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100" data-id="element-176">
                  <h3 className="font-semibold text-slate-900 text-sm" data-id="element-177">
                    Notifications
                  </h3>
                  <Link to="/notifications" onClick={() => setShowNotifDropdown(false)} className="text-xs font-medium text-navy-700 hover:text-navy-900" data-id="element-178">
                    View All
                  </Link>
                </div>
                <div className="max-h-80 overflow-y-auto" data-id="element-179">
                  {recentNotifs.map(notif => <button key={notif.id} onClick={() => {
                markNotificationRead(notif.id);
                setShowNotifDropdown(false);
              }} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!notif.read ? 'bg-navy-50/30' : ''}`} data-id="element-180">
                      <div className="flex items-start gap-2" data-id="element-181">
                        {!notif.read && <div className="mt-1.5 h-2 w-2 rounded-full bg-navy-700 flex-shrink-0" data-id="element-182" />}
                        <div className="flex-1 min-w-0" data-id="element-183">
                          <p className={`text-xs font-semibold truncate ${!notif.read ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-184">
                            {notif.title}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2" data-id="element-185">
                            {notif.desc}
                          </p>
                          <div className="flex items-center gap-2 mt-1" data-id="element-186">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${notif.category === 'marketing' ? 'bg-purple-100 text-purple-700' : notif.category === 'operational' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-187">
                              {notif.category || 'system'}
                            </span>
                            <span className="text-[10px] text-slate-400" data-id="element-188">
                              {notif.time}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>)}
                </div>
              </div>
            </>}
        </div>

        <div className="h-8 w-px bg-slate-200 mx-0.5 sm:mx-1 hidden sm:block" data-id="element-189" />

        <div className="flex items-center gap-2" data-id="element-190">
          <div className="flex flex-col items-end" data-id="element-191">
            <span className="text-sm font-medium text-slate-700 hidden sm:block" data-id="element-192">
              Acme Corp
            </span>
          </div>
          {getRoleBadge()}
          <div className="h-8 w-8 rounded bg-navy-900 flex items-center justify-center text-gold-400 font-bold text-xs ml-1 sm:ml-2" data-id="element-193">
            AC
          </div>
        </div>
      </div>
    </header>;
}